<template>
  <core-modal-header>Edit Profile</core-modal-header>

  <template v-if="isOrganization">
    <core-modal-body>
      <user-edit-organization-info-form
        :show-submit="false"
        ref="organizationFormComp"
      />
    </core-modal-body>

    <core-modal-footer>
      <u-button variant="soft" color="charcoal" @click="modal.close()">Cancel</u-button>
      <u-button @click="submitOrganizationForm" :loading="isSaving">Update Profile</u-button>
    </core-modal-footer>

  </template>

  <template v-else>
    <u-form @submit="submit" :schema="schema" :state="state" :validate-on="['submit']">

      <core-modal-body>
        <u-form-group label="Your Name" name="name" class="mb-4" required>
          <u-input v-model="state.name" placeholder="Your Name" />
        </u-form-group>

        <u-form-group label="Location" name="location" class="mb-4" hint="Optional">
          <core-location-input v-model="state.location" />
        </u-form-group>

        <u-form-group label="Bio" name="bio" class="mb-4">
          <template #hint>
            <core-input-length-counter v-model="state.bio" :limit="SESSION_BIO_MAX_LENGTH" :display-at="SESSION_BIO_MAX_LENGTH * 0.8" />
          </template>
          <u-textarea v-model="state.bio" :rows="7" :maxrows="14" autoresize :maxlength="SESSION_BIO_MAX_LENGTH" placeholder="Add a little about yourself for others to see." />
        </u-form-group>
      </core-modal-body>

      <core-modal-footer>
        <u-button variant="soft" color="charcoal" @click="modal.close()">Cancel</u-button>
        <u-button type="submit" :loading="isSaving">Update Profile</u-button>
      </core-modal-footer>

    </u-form>
  </template>

</template>

<script setup>
  import {object, string, boolean} from 'yup';

  const props = defineProps({
    user: Object,
    modal: Object
  });

  const sessionStore = useSessionStore();
  const {currentUser} = storeToRefs(sessionStore);
  const isOrganization = computed(() => currentUser.value.account_type === USER_ACCOUNT_TYPES.organization.value);

  const isSaving = ref(false);

  //todo: update schema to match organization fields
  const schema = object({
    name: string().required('Name is required.').max(SESSION_USERNAME_MAX_LENGTH, `Max length cannot exceed ${SESSION_USERNAME_MAX_LENGTH} characters.`),
    location: string().nullable(true).max(SESSION_LOCATION_MAX_LENGTH, `Max length cannot exceed ${SESSION_LOCATION_MAX_LENGTH} characters.`),
    bio: string().nullable(true).max(SESSION_BIO_MAX_LENGTH, `Max length cannot exceed ${SESSION_BIO_MAX_LENGTH} characters.`),
    private: boolean()
  });

  const state = reactive({
    name: props.user.name,
    location: props.user.location || '',
    bio: props.user.bio,
    private: props.user.private,
    id: props.user.id
  });

  async function submit() {
    try {
      isSaving.value = true;
      await useSessionStore().updateCurrentUser({
        ...state,
        location: state.location?.trim() || null
      });
      useSuccessToast().add({
        id: 'profile-add-edit',
        title: `Profile Updated`,
        description: `Your profile has been updated.`
      });
      props.modal.close();
    } catch (e) {
      useErrorToast().add();
    } finally {
      isSaving.value = false;
    }
  }

  const organizationFormComp = ref();

  async function submitOrganizationForm() {
    try {
      isSaving.value = true;

      await organizationFormComp.value.save();

      props.modal.close();
    } catch (e) {
      useErrorToast().add();
    } finally {
      isSaving.value = false;
    }
  }

</script>
